/** P32 (Feb 27, 2026) — Patient autocomplete (name / Medicaid ID / DOB). */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Input } from '../../../components/ui/input';

function PatientAutocomplete({ patients, selected, onSelect, disabled }) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    if (selected) {
      const dob = selected.date_of_birth ? ` · ${selected.date_of_birth}` : '';
      setQuery(`${selected.last_name}, ${selected.first_name}${dob}`);
    } else {
      setQuery('');
    }
  }, [selected]);

  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // P28: Pre-filter the active patient subset once; matching the search
  // query against an already-filtered list is significantly faster than
  // chaining two .filter() calls on every keystroke.
  const activePatients = useMemo(
    () => patients.filter((p) => p.status === 'Active'),
    [patients]
  );

  const matches = useMemo(() => {
    const q = (query || '').trim().toLowerCase();
    if (!q) return activePatients.slice(0, 8);
    const isDigit = /^\d+$/.test(q);
    const out = [];
    for (const p of activePatients) {
      if (isDigit) {
        if (String(p.medicaid_id || '').includes(q)) out.push(p);
      } else {
        const fullName = `${p.last_name || ''} ${p.first_name || ''}`.toLowerCase();
        if (
          fullName.includes(q)
          || String(p.medicaid_id || '').toLowerCase().includes(q)
          || String(p.date_of_birth || '').includes(q)
        ) out.push(p);
      }
      if (out.length >= 10) break;  // early exit — caps work on huge rosters
    }
    return out;
  }, [activePatients, query]);

  return (
    <div className="relative" ref={containerRef} data-testid="patient-autocomplete">
      <Input
        type="text"
        value={query}
        onChange={(e) => { setQuery(e.target.value); setOpen(true); if (selected) onSelect(null); }}
        onFocus={() => setOpen(true)}
        placeholder="Start typing a name, Medicaid ID or DOB…"
        disabled={disabled}
        data-testid="patient-search-input"
      />
      {open && !disabled && matches.length > 0 && (
        <div className="absolute z-20 mt-1 w-full max-h-72 overflow-y-auto bg-white border border-slate-200 rounded-md shadow-lg" data-testid="patient-search-dropdown">
          {matches.map((p) => (
            <button
              type="button"
              key={p.id}
              className="w-full text-left px-3 py-2 hover:bg-indigo-50 border-b border-slate-100 last:border-b-0"
              onClick={() => { onSelect(p); setOpen(false); }}
              data-testid={`patient-option-${p.id}`}
            >
              <div className="font-medium text-slate-900 text-sm">
                {p.last_name}, {p.first_name}
              </div>
              <div className="text-xs text-slate-500">
                MID {p.medicaid_id ? `••${String(p.medicaid_id).slice(-4)}` : '—'} · DOB {p.date_of_birth || '—'} · {p.county || 'No county'}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default PatientAutocomplete;
